<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="8">
        <div class="mb-8">
          <h1 class="text-center">Dodaj Wpis do Księgi</h1>
        </div>

        <v-form @submit.prevent="addPost" class="mb-8">
          <v-row>
            <v-col cols="12">
              <!-- Ukryj pole do przesyłania pliku, jeśli plik został załadowany -->
              <v-file-input
                v-if="!fileUrl"
                ref="fileInput"
                label="Dodaj zdjęcie lub wideo"
                @change="handleFileUpload"
                prepend-icon=""
                accept="image/*,android/force-camera-workaround"
                :error="fileError"
              ></v-file-input>

              <!-- Podgląd zdjęcia lub wideo z przyciskiem "X" -->
              <div v-if="fileUrl" class="file-preview-container">
                <template v-if="isVideo(fileType)">
                  <video
                    controls
                    class="file-preview"
                    :src="fileUrl"
                  ></video>
                </template>
                <template v-else>
                  <v-img
                    :src="fileUrl"
                    alt="Podgląd Pliku"
                    class="file-preview"
                  ></v-img>
                </template>
                <v-btn
                  icon
                  class="remove-file-btn"
                  @click="removeFile"
                  color="red"
                >
                  X
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12">
              <v-text-field
                label="Twoje Imię"
                v-model="newPost.author"
                variant="outlined"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                label="Opis (opcjonalnie)"
                v-model="newPost.content"
                rows="3"
                variant="outlined"
                required
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-btn
                type="submit"
                size="x-large"
                :loading="isUploading"
                :disabled="isUploading"
                color="primary"
                block
              >
                Dodaj Wpis
              </v-btn>
            </v-col>
            <v-col cols="12" v-if="uploadProgress > 0 && uploadProgress < 100">
              <v-progress-linear
                :model-value="uploadProgress"
                color="blue"
                height="6"
                class="mt-3"
              ></v-progress-linear>
              <p class="text-center">
                Wysyłanie pliku: {{ Math.round(uploadProgress) }}%
              </p>
            </v-col>
            <v-col cols="12" v-if="uploadError">
              <p class="text-center" style="color: red">
                Błąd podczas wysyłania pliku: {{ uploadError }}
              </p>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, onMounted } from "vue";
import { db, storage } from "@/firebaseConfig";
import { collection, addDoc } from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { useRouter } from "vue-router";

export default {
  setup() {
    const newPost = ref({ author: "", content: "", fileUrl: null });
    const file = ref(null);
    const fileUrl = ref(null);
    const fileType = ref(null);
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const uploadError = ref(null);
    const fileInput = ref(null);
    const router = useRouter();
    const fileError = ref(false);

    // Funkcja do obsługi zmiany pliku
    const handleFileUpload = (event) => {
      file.value = event.target.files[0];
      if (file.value) {
        fileUrl.value = URL.createObjectURL(file.value);
        fileType.value = file.value.type;
        fileError.value = false;
      } else {
        fileUrl.value = null;
        fileType.value = null;
      }
    };

    // Funkcja do usuwania pliku
    const removeFile = () => {
      file.value = null;
      fileUrl.value = null;
      fileType.value = null;
      fileError.value = false;
    };

    // Funkcja do przesyłania pliku
    const uploadFile = async () => {
      if (!file.value) {
        return null;
      }

      isUploading.value = true;
      uploadProgress.value = 0;
      uploadError.value = null;

      const storageReference = storageRef(
        storage,
        `images/${Date.now()}_${file.value.name}`
      );
      const uploadTask = uploadBytesResumable(storageReference, file.value);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            uploadProgress.value =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          },
          (error) => {
            isUploading.value = false;
            uploadError.value = error.message;
            console.error("File upload error:", error);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            isUploading.value = false;
            resolve(downloadURL);
          }
        );
      });
    };

    // Funkcja do dodawania wpisu
    const addPost = async () => {
      if (!file.value) {
        fileError.value = true;
        return;
      }

      const uploadedFileUrl = await uploadFile();
      try {
        const postsCollection = collection(db, "posts");
        await addDoc(postsCollection, {
          author: newPost.value.author,
          content: newPost.value.content.trim() || null,
          fileUrl: uploadedFileUrl,
          fileType: fileType.value,
          createdAt: new Date(),
        });

        if (newPost.value.author) {
          localStorage.setItem("guestbookAuthor", newPost.value.author);
        }

        console.log("Wpis dodany pomyślnie!");
        router.push("/");
      } catch (error) {
        console.error("Błąd podczas dodawania wpisu:", error);
        alert("Nie udało się dodać wpisu.");
      }
    };

    const isVideo = (type) => {
      return type && type.startsWith("video/");
    };

    onMounted(() => {
      const savedAuthor = localStorage.getItem("guestbookAuthor");
      if (savedAuthor) {
        newPost.value.author = savedAuthor;
      }

      if (fileInput.value) {
        fileInput.value.$el.querySelector("input").click();
      }
    });

    return {
      newPost,
      fileUrl,
      fileType,
      handleFileUpload,
      removeFile,
      addPost,
      isUploading,
      uploadProgress,
      uploadError,
      fileInput,
      fileError,
      isVideo,
    };
  },
};
</script>

<style scoped>
.file-preview-container {
  position: relative;
}

.file-preview {
  width: 100%;
  max-height: 70vh;
}

.remove-file-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: white;
  color: red;
  z-index: 10;
}
</style>
